/** @jsxImportSource @emotion/react */

import { useEffect, useMemo, useState } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { Avatar, Button, IconButton, Typography } from '@mui/material'
import { keyBy, last, sortBy } from 'lodash'
import { InfluencerTypeEnum, PlatformName } from '__generatedTypes__/globalTypes'
import { PageLoader } from 'components/PageLoader'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { BULK_UPDATE_INFLUENCER_HUB, FIND_ALL_INFLUENCER_HUB, REMOVE_INFLUENCER_HUB } from 'queries/influencerHub'
import {
  FindAllInfluencerHub,
  FindAllInfluencerHubVariables,
  FindAllInfluencerHub_findAllInfluencerHub_items as Influencer,
} from '__generatedTypes__/FindAllInfluencerHub'
import { DescriptionForm, DescriptionPages } from 'components/DescriptionForm'
import { ReactComponent as IconPlus } from 'assets/icons/plus.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import Table from 'components/Table'
import { toast } from 'utils/toast'
import { RemoveInfluencerHub, RemoveInfluencerHubVariables } from '__generatedTypes__/RemoveInfluencerHub'
import { BulkUpdateInfluencerHub, BulkUpdateInfluencerHubVariables } from '__generatedTypes__/BulkUpdateInfluencerHub'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import * as classes from './styles'
import { InfluencerFormModal, InitialValues } from './components/InfluencerFormModal'

const INFLUENCER_TYPES_ORDER = [
  InfluencerTypeEnum.FeaturedInfluencer,
  InfluencerTypeEnum.PreMedicalStudentInfluencer,
  InfluencerTypeEnum.MedicalStudentInfluencer,
  InfluencerTypeEnum.DoctorInfluencer,
]

const INFLUENCER_TYPE_TITLE = {
  [InfluencerTypeEnum.FeaturedInfluencer]: 'Featured Influencer',
  [InfluencerTypeEnum.PreMedicalStudentInfluencer]: 'Pre-Medical Student Influencer',
  [InfluencerTypeEnum.MedicalStudentInfluencer]: 'Medical Student Influencer',
  [InfluencerTypeEnum.DoctorInfluencer]: 'Doctor Influencer',
}

export const InfluencerHub = () => {
  const apolloClient = useApolloClient()

  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [formInitialValues, setFormInitialValues] = useState<InitialValues | null>(null)

  const [deleteInfluencerId, setDeleteInfluencerId] = useState<string | null>(null)

  const { selectedPlatform } = usePlatformsAndSubjects({})
  const { navigate } = useLink()

  const { data, loading } = useQuery<FindAllInfluencerHub, FindAllInfluencerHubVariables>(FIND_ALL_INFLUENCER_HUB, {
    variables: {
      query: {},
    },
    fetchPolicy: 'network-only',
  })

  const [deleteInfluencer] = useMutation<RemoveInfluencerHub, RemoveInfluencerHubVariables>(REMOVE_INFLUENCER_HUB)
  const [bulkUpdate] = useMutation<BulkUpdateInfluencerHub, BulkUpdateInfluencerHubVariables>(
    BULK_UPDATE_INFLUENCER_HUB
  )

  useEffect(() => {
    if (selectedPlatform && selectedPlatform.title !== PlatformName.CARSBooster) {
      navigate(`/${String(selectedPlatform.slug)}/${cmsRoutes.NOT_FOUND}`)
    }
  }, [selectedPlatform?.title])

  const handleOpenModal = (initialValues?: InitialValues) => {
    setIsFormModalOpen(true)
    setFormInitialValues(initialValues || null)
  }

  const handleCloseModal = () => {
    setIsFormModalOpen(false)
    setFormInitialValues(null)
  }

  const influencerTypes = useMemo(() => {
    if (!data) {
      return {}
    }

    return data.findAllInfluencerHub.items.reduce(
      (acc, item) => ({
        ...acc,
        [item.influencerType]: sortBy([...(acc[item.influencerType] || []), item], 'order'),
      }),
      {} as Record<string, FindAllInfluencerHub['findAllInfluencerHub']['items']>
    )
  }, [data])

  if (!selectedPlatform || loading) {
    return <PageLoader />
  }

  const handleSort = async (influencersOrder: Influencer[]) => {
    try {
      const influencersWithNewOrder = influencersOrder.map((influencer, index) => ({
        ...influencer,
        order: index,
      }))

      const grouped = keyBy(influencersWithNewOrder, 'id')

      apolloClient.cache.updateQuery(
        {
          query: FIND_ALL_INFLUENCER_HUB,
          variables: {
            query: {},
          },
        },
        (data) => ({
          findAllInfluencerHub: {
            count: data.findAllInfluencerHub,
            items: data.findAllInfluencerHub.items.map((item: Influencer) => grouped[item.id] || item),
          },
        })
      )

      await bulkUpdate({
        variables: {
          bulkUpdateInfluencerHubInput: influencersWithNewOrder.map(({ id, order }) => ({
            id,
            order,
          })),
        },
      })
      toast.success('Order updated successfully')
    } catch (e) {
      toast.error('Failed to update order')
    }
  }

  const handleDeleteInfluencer = async (id: string) => {
    apolloClient.cache.evict({
      id: apolloClient.cache.identify({
        __typename: 'InfluencerHubType',
        id,
      }),
    })

    setDeleteInfluencerId(null)

    try {
      await deleteInfluencer({
        variables: {
          id,
        },
      })
      toast.success('Influencer deleted successfully')
    } catch {
      toast.error('Failed to delete influencer, please reload the page')
    }
  }

  const renderInfluencerType = (influencerType: InfluencerTypeEnum) => {
    const items = influencerTypes[influencerType] || []

    if (!items.length) {
      return (
        <Typography variant="subtitle1" css={classes.noInfluencersFound} data-testid="no-items">
          No influencers found
        </Typography>
      )
    }

    return (
      <Table
        css={classes.influencersTable}
        hideHeader
        isSortable
        onSort={handleSort}
        columns={[
          {
            title: 'Index',
            renderCell: (_, index) => <span css={classes.indexCell}>{index + 1}</span>,
            flex: '0 1 30px',
          },
          {
            title: 'Items Left',
            renderCell: (influencer) => <Avatar src={influencer.coverImage} css={classes.coverImage} alt="Photo" />,
            flex: '0 1 32px',
          },
          {
            title: 'Items Left',
            renderCell: (influencer) => <span>{influencer.name}</span>,
            flex: 1,
          },
          {
            renderCell: (influencer) => (
              <IconButton css={classes.iconButton} onClick={() => handleOpenModal(influencer)}>
                <EditIcon />
              </IconButton>
            ),
            flex: '0 1 29px',
          },
          {
            renderCell: (influencer) => (
              <IconButton css={classes.iconButton} onClick={() => setDeleteInfluencerId(influencer.id)}>
                <CrossIcon />
              </IconButton>
            ),
            flex: '0 1 29px',
          },
        ]}
        items={items}
        wrapperMinWidth={500}
        wrapperPadding={8}
        data-testid="items"
      />
    )
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        Influencer Hub
      </Typography>

      <div css={classes.content}>
        <DescriptionForm page={DescriptionPages.InfluencerHub} title="Page Description" />

        {INFLUENCER_TYPES_ORDER.map((influencerType) => {
          const isFeauturedInfluencer = influencerType === InfluencerTypeEnum.FeaturedInfluencer
          return (
            <section key={influencerType} css={classes.influencerTypeWrapper}>
              <Typography variant="body2">{INFLUENCER_TYPE_TITLE[influencerType]}</Typography>
              <div css={classes.influencerTypeContent}>
                {renderInfluencerType(influencerType)}

                <Button
                  variant="outlined"
                  color="blue"
                  size="large"
                  fullWidth
                  css={classes.addInfluencerButton}
                  disabled={isFeauturedInfluencer && influencerTypes[influencerType]?.length >= 3}
                  onClick={() =>
                    handleOpenModal({
                      influencerType,
                      order: (last(influencerTypes[influencerType])?.order || 0) + 1,
                    })
                  }
                >
                  Add {isFeauturedInfluencer ? 'Featured' : ''} Influencer <IconPlus />
                </Button>
              </div>
            </section>
          )
        })}
      </div>

      <InfluencerFormModal isOpen={isFormModalOpen} onClose={handleCloseModal} initialValues={formInitialValues} />

      <ConfirmationModal
        open={deleteInfluencerId !== null}
        title="Deleting influencer"
        subtitle="Are you sure you want to delete this record?"
        onClose={() => setDeleteInfluencerId(null)}
        onConfirm={() => handleDeleteInfluencer(deleteInfluencerId || '')}
        confirmText="Delete"
        closeText="Cancel"
      />
    </section>
  )
}
